import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { TextInputProps } from '@toasttab/shared/src/interfaces/Interface.d'

const TextInput = ({
  classes,
  disabled,
  error,
  fullWidth,
  id,
  label,
  name,
  onBlur,
  onChange,
  onFocus,
  onKeyUp,
  padding = '',
  placeholder,
  required,
  type = 'text',
  value,
  variation = 'default',
  inputRef,
  interaction = '',
  ...rest
}: TextInputProps) => {
  const [hasValue, setHasValue] = useState(false)

  // We need this useEffect call to check the initial input state for the form and apply the correct styling.
  // Sometimes autofill values aren't properly handled on load, which leads to the label overlapping the text.
  // This forces the component to check that value and trigger a rerender of the component to ensures the proper styling.
  useEffect(() => {
    // The !! forces this to a boolean for our state value
    setHasValue(!!value)
  }, [value])

  const labelClassNames = classNames({
    hidden: !label && variation !== 'default' && variation !== 'inverse',
    'font-sansPro font-normal text-left text-secondary tracking-[0.2px] pointer-events-none h-[24px] mb-[3px]':
      true,
    'text-base leading-[20px]':
      label &&
      (variation === 'inverse' || variation === 'default') &&
      !hasValue,
    'absolute top-[15px] left-[10px] right-[10px] peer-focus/input:text-[10px] peer-focus/input:leading-[10px] peer-focus/input:top-[10px]':
      label &&
      (variation === 'inverse' || variation === 'default') &&
      padding === 'large' &&
      !hasValue,
    'absolute top-[12px] left-4 right-4 peer-focus/input:text-[10px] peer-focus/input:leading-[10px] peer-focus/input:top-[10px]':
      label &&
      (variation === 'inverse' || variation === 'default') &&
      padding === 'medium' &&
      !hasValue,
    'absolute left-[10px] right-[10px] text-[10px] leading-[10px] top-[10px]':
      label &&
      (variation === 'inverse' || variation === 'default') &&
      padding === 'large' &&
      hasValue
  })

  const inputClassNames = classNames(
    classes,
    'bg-white text-base placeholder:text-gray-50 text-default rounded-[4px] font-sansPro',
    {
      // Full Width
      'w-full': fullWidth,
      // Border shadow
      'border border-gray-50 shadow-none text-base box-border peer/input':
        (variation === 'inverse' || variation === 'default') && !error,
      'placeholder-transparent':
        (variation === 'inverse' || variation === 'default') && label !== '',
      'px-2 py-[9px] border border-gray-50 shadow-none rounded-none focus:outline-none focus:shadow-text-input text-lg':
        variation === 'otl-default' && !error,
      'px-2 py-[9px] border border-otl-blue shadow-none rounded-none focus:outline-none focus:shadow-text-input text-lg':
        variation === 'otl-blue' && !error,
      'border border-otl-error border-error': error,
      'rounded-none': variation === 'otl-default' && error,

      // Padding
      'px-2 py-[9px]':
        padding !== '' && variation !== 'inverse' && variation !== 'default',
      'py-3 px-[10px] max-h-[50px]':
        padding === 'large' &&
        (variation === 'inverse' || variation === 'default'),
      // Focus Padding
      'focus-within:pt-[18px] focus-within:pb-[6px]':
        padding === 'large' &&
        (variation === 'inverse' || variation === 'default') &&
        label,
      'pt-[18px] pb-[6px]':
        padding === 'large' &&
        (variation === 'inverse' || variation === 'default') &&
        label &&
        hasValue,

      'py-3 px-4 max-h-[48px]':
        padding === 'medium' &&
        (variation === 'inverse' || variation === 'default')
    }
  )

  const errorClassNames = classNames({
    'text-otl-error text-xs top-12 left-0':
      variation !== 'default' && variation !== 'inverse',
    'absolute top-full w-max flex flex-row font-sansPro items-center text-error text-xs font-semibold left-0 leading-4 xl:-mb-[6px] before:content-[""] before:bg-icon-warning-outline before:bg-center before:bg-contain before:bg-no-repeat before:w-4 before:h-4 before:block before:mr-2':
      variation === 'default',
    'absolute top-full w-max flex flex-row font-sansPro items-center text-white text-xs font-semibold left-0 leading-4 xl:-mb-[6px] before:content-[""] before:bg-icon-warning-outline-inverse before:bg-center before:bg-contain before:bg-no-repeat before:w-4 before:h-4 before:block before:mr-2':
      variation === 'inverse'
  })

  return (
    <>
      <input
        aria-label={!label ? placeholder : null}
        className={inputClassNames}
        data-interaction={interaction}
        disabled={disabled}
        id={id}
        maxLength={255}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        onKeyUp={onKeyUp}
        placeholder={!label ? placeholder : null}
        ref={inputRef}
        required={required}
        type={type}
        value={value}
        {...rest}
      />
      {label ? (
        <label className={labelClassNames} htmlFor={id}>
          {label}
        </label>
      ) : null}
      {error ? (
        <div className={errorClassNames} role='alert'>
          {error}
        </div>
      ) : null}
    </>
  )
}

export default TextInput
